import {
  KVAnnotation,
  KVMeasurement,
  KVMeasurementJSON,
  KVPrimitive,
  RolodexEntryJson,
} from 'types'
import { GeckoEntryBase } from 'types/entries/geckoEntry'
import { KVInspectionArea } from 'types/kestrelViewer/KVInspectionArea'
import { parseString } from 'utils/parsingHelpers'

/**
 * A separate Rolodex entry that stores whiteboard-specific data:
 * primitives, annotations, inspection areas, and measurements.
 *
 * Tagged to its parent kestrel-model entry.
 */
export class GeckoKestrelModelWhiteboard extends GeckoEntryBase<
  {
    name: string
    annotations: KVAnnotation[]
    measurements: KVMeasurement[]
    inspectionAreas: KVInspectionArea[]
    primitives: KVPrimitive[]
  },
  {
    'kestrel-model': string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const annotations = new Array<KVAnnotation>()
    const measurements = new Array<KVMeasurement>()
    const inspectionAreas = new Array<KVInspectionArea>()
    const primitives = new Array<KVPrimitive>()

    const name = (recordData?.name as string) ?? ''

    if (recordData.annotations) {
      const annotationJSONs = recordData.annotations as Record<
        string,
        unknown
      >[]
      for (const annotation of annotationJSONs) {
        annotations.push(KVAnnotation.fromJSON(annotation))
      }
    }

    if (recordData.measurements) {
      const measurementJSONs = recordData.measurements as KVMeasurementJSON[]
      for (const measurement of measurementJSONs) {
        measurements.push(KVMeasurement.fromJSON(measurement))
      }
    }

    if (recordData.inspectionAreas) {
      const inspectionAreaJSONs = recordData.inspectionAreas as Record<
        string,
        unknown
      >[]
      for (const inspectionArea of inspectionAreaJSONs) {
        inspectionAreas.push(KVInspectionArea.fromJSON(inspectionArea))
      }
    }

    if (recordData.primitives) {
      const primitiveJSONs = recordData.primitives as Record<string, unknown>[]
      for (const primitive of primitiveJSONs) {
        primitives.push(KVPrimitive.fromJSON(primitive))
      }
    }

    super(
      json,
      {
        name,
        annotations,
        measurements,
        inspectionAreas,
        primitives,
      },
      {
        'kestrel-model': parseString(json.tags['kestrel-model']) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoKestrelModelWhiteboard {
    return new GeckoKestrelModelWhiteboard(json)
  }
}
